import React from "react";
import { useHistory } from "react-router-dom";
import {
  ArrowRight,
  BarChart3,
  Brain,
  ChevronRight,
  DollarSign,
  FileDown,
  GraduationCap,
  LayoutGrid,
  type LucideIcon,
  PieChart,
  Wallet,
} from "lucide-react";
import "./analytics-hub.css";

type Category = "ai" | "learning" | "engagement" | "executive" | "operations" | "finance";

interface Tile {
  id: string;
  title: string;
  desc: string;
  to: string;
  icon: LucideIcon;
  category: Category;
  badge?: "new" | "beta";
}

const SECTIONS: { label: string; tiles: Tile[] }[] = [
  {
    label: "Learning",
    tiles: [
      {
        id: "learning-analytics",
        title: "Learning Workspace",
        desc: "Filtered workspace for every learning-side dashboard — student practice, pathway analytics, flip-class engagement, LMS content. Set a date/batch scope once and it flows into whatever report you open.",
        to: "/learning-analytics",
        icon: GraduationCap,
        category: "learning",
      },
    ],
  },
  {
    label: "AI Governance",
    tiles: [
      {
        id: "ai-usage",
        title: "AI Usage",
        desc: "Token consumption, feature mix, per-user usage, and quota status — the evidence layer for AI governance.",
        to: "/ai-usage-dashboard",
        icon: Brain,
        category: "ai",
      },
      {
        id: "ai-subscriptions",
        title: "AI Providers",
        desc: "Anthropic, OpenAI & Pinecone connection health, the tenant's default model, and the monthly spend cap. Now under Tenant Preferences.",
        to: "/tenant-preferences?tab=ai-providers",
        icon: Wallet,
        category: "finance",
      },
    ],
  },
  {
    label: "Executive & Operations",
    tiles: [
      {
        id: "executive-dashboard",
        title: "Executive Dashboard",
        desc: "Cross-functional KPIs — enrollments, revenue, NPS, retention — tailored for institutional leadership.",
        to: "/executive-dashboard",
        icon: BarChart3,
        category: "executive",
      },
      {
        id: "academic-dashboard",
        title: "Academic Dashboard",
        desc: "School/program/course pivots over the academic operating picture — enrollment, results, attendance.",
        to: "/academic-dashboard",
        icon: PieChart,
        category: "operations",
      },
      {
        id: "director-dashboard",
        title: "Director Dashboard",
        desc: "Director-level matrix, school view, course view, and batch drilldowns across academic units.",
        to: "/director-dashboard",
        icon: PieChart,
        category: "operations",
      },
      {
        id: "grid-report",
        title: "Grid Report",
        desc: "Generic pivot reporter — point at any saved query, slice and dice by any dimension.",
        to: "/grid-report",
        icon: LayoutGrid,
        category: "operations",
      },
      {
        id: "admin-usage",
        title: "Dossier & Export Usage",
        desc: "Dossier generations, exports, and downloads across the tenant — top templates, top users, and per-dossier audit drill-down.",
        to: "/admin/usage",
        icon: FileDown,
        category: "operations",
      },
    ],
  },
  {
    label: "Finance",
    tiles: [
      {
        id: "ar-reports",
        title: "AR Reports",
        desc: "Aged receivables, defaulter views, instalment-level outstanding by program and cohort.",
        to: "/ar-reports",
        icon: DollarSign,
        category: "finance",
      },
      {
        id: "collections",
        title: "Daily Collections",
        desc: "Payments received today, this week, this month — segmented by gateway, instrument, and tenant.",
        to: "/financial-reports/collections",
        icon: Wallet,
        category: "finance",
      },
      {
        id: "defaulters",
        title: "Defaulter Report",
        desc: "Students with overdue instalments — prioritised for collections workflows.",
        to: "/financial-reports/defaulters",
        icon: DollarSign,
        category: "finance",
      },
    ],
  },
];

const RELATED: { label: string; to: string }[] = [
  { label: "Governance Console", to: "/governance-console" },
  { label: "Executive Flip Dashboard", to: "/executive-flip-dashboard" },
  { label: "Tenant Preferences", to: "/tenant-preferences" },
];

const AnalyticsHubPage: React.FC = () => {
  const history = useHistory();

  return (
    <div className="ana-hub">
      <div className="ana-header">
        <div>
          <button
            onClick={() => history.push("/tenant-preferences")}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              padding: "6px 10px",
              background: "var(--brand-primary-subtle)",
              border: "1px solid var(--brand-primary-subtle)",
              borderRadius: 8,
              cursor: "pointer",
              fontSize: 13,
              color: "var(--brand-primary-hover)",
              fontWeight: 500,
              marginBottom: 8,
            }}
            title="Tenant Preferences — branding, receipts, theme"
          >
            ⚙ Tenant Preferences
          </button>
          <h1>
            <span className="ana-header-icon"><BarChart3 size={22} /></span>
            Reports Directory
          </h1>
          <p className="ana-subtitle">
            Index of every operational, executive, and financial report. Click a tile to open its
            dashboard. Learning-side reports (student practice, flip-class, LMS) live in the{" "}
            <button
              type="button"
              onClick={() => history.push("/learning-analytics")}
              style={{ background: "none", border: "none", padding: 0, color: "var(--brand-primary)", textDecoration: "underline", cursor: "pointer", font: "inherit" }}
            >
              Learning Workspace
            </button>{" "}
            — go there to filter by date/batch and drill in.
          </p>
        </div>
      </div>

      {SECTIONS.map((section) => (
        <React.Fragment key={section.label}>
          <div className="ana-section-label">{section.label}</div>
          <div className="ana-grid">
            {section.tiles.map((tile) => {
              const Icon = tile.icon;
              return (
                <button
                  key={tile.id}
                  type="button"
                  className="ana-tile"
                  onClick={() => history.push(tile.to)}
                >
                  <div className="ana-tile-head">
                    <div className={`ana-tile-icon ${tile.category}`}>
                      <Icon size={22} />
                    </div>
                    {tile.badge && (
                      <span className={`ana-tile-badge ${tile.badge}`}>{tile.badge}</span>
                    )}
                  </div>
                  <h3 className="ana-tile-title">{tile.title}</h3>
                  <p className="ana-tile-desc">{tile.desc}</p>
                  <div className="ana-tile-meta">
                    <span className="ana-tile-url">{tile.to}</span>
                    <span className="ana-tile-arrow">
                      Open <ChevronRight size={12} />
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </React.Fragment>
      ))}

      <div className="ana-related">
        <span className="ana-related-label">Related</span>
        {RELATED.map((r) => (
          <button
            key={r.to}
            type="button"
            className="ana-related-link"
            onClick={() => history.push(r.to)}
          >
            {r.label} <ArrowRight size={12} />
          </button>
        ))}
      </div>
    </div>
  );
};

export default AnalyticsHubPage;
